import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { createUser, getAllScreens, getAllFeatures } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';

const AddUser = ({ onClose, onUserAdded }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    role: 'viewer'
  });
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [screens, setScreens] = useState([]);
  const [features, setFeatures] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [submitError, setSubmitError] = useState('');

  const roleOptions = [
    { value: 'admin', label: 'Admin - Full system access' },
    { value: 'manager', label: 'Manager - Business management access' },
    { value: 'analyst', label: 'Analyst - Data analysis access' },
    { value: 'viewer', label: 'Viewer - Read-only access' }
  ];

  useEffect(() => {
    loadScreensAndFeatures();
  }, []);

  const loadScreensAndFeatures = async () => {
    try {
      setLoadingData(true);
      const [screensData, featuresData] = await Promise.all([
        getAllScreens(),
        getAllFeatures()
      ]);
      setScreens(screensData || []);
      setFeatures(featuresData || []);
    } catch (error) {
      console.error('Error loading screens and features:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleScreenToggle = (screenId) => {
    setSelectedScreens(prev => {
      if (prev?.includes(screenId)) {
        return prev?.filter(id => id !== screenId);
      }
      return [...prev, screenId];
    });
  };

  const handleFeatureToggle = (featureId) => {
    setSelectedFeatures(prev => {
      if (prev?.includes(featureId)) {
        return prev?.filter(id => id !== featureId);
      }
      return [...prev, featureId];
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const userData = {
        email: formData?.email,
        password: formData?.password,
        fullName: formData?.fullName,
        role: formData?.role,
        screenPermissions: selectedScreens,
        featurePermissions: selectedFeatures
      };

      const newUser = await createUser(userData);
      
      if (onUserAdded) {
        onUserAdded(newUser);
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      setSubmitError(error?.message || 'Failed to create user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!userProfile?.role || userProfile?.role !== 'admin') {
    return (
      <div className="text-center py-8">
        <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">You do not have permission to add users.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="UserPlus" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Add New User</h2>
            <p className="text-sm text-muted-foreground">Create a new user account with permissions</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
            disabled={isLoading}
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {submitError && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="User" size={16} />
            Basic Information
          </h3>
          
          <Input
            type="text"
            name="fullName"
            label="Full Name"
            placeholder="Enter full name"
            value={formData?.fullName}
            onChange={handleInputChange}
            error={errors?.fullName}
            required
            disabled={isLoading}
          />

          <Input
            type="email"
            name="email"
            label="Email Address"
            placeholder="Enter email address"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
            disabled={isLoading}
          />

          <Input
            type="password"
            name="password"
            label="Password"
            placeholder="Enter password (min 6 characters)"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />

          <Select
            name="role"
            label="User Role"
            value={formData?.role}
            onChange={handleInputChange}
            options={roleOptions}
            error={errors?.role}
            required
            disabled={isLoading}
          />
        </div>

        {/* Screen Permissions */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Monitor" size={16} />
            Screen Access Permissions
          </h3>
          
          {loadingData ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-2 bg-muted/30 rounded-md p-4">
              {screens?.length > 0 ? (
                screens?.map((screen) => (
                  <div key={screen?.id} className="flex items-start gap-3 p-3 bg-card rounded-md border border-border hover:border-primary/30 transition-smooth">
                    <Checkbox
                      checked={selectedScreens?.includes(screen?.id)}
                      onChange={() => handleScreenToggle(screen?.id)}
                      disabled={isLoading}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{screen?.displayName}</p>
                      {screen?.description && (
                        <p className="text-xs text-muted-foreground mt-1">{screen?.description}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No screens available</p>
              )}
            </div>
          )}
        </div>

        {/* Feature Permissions */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Zap" size={16} />
            Feature Access Permissions
          </h3>
          
          {loadingData ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-2 bg-muted/30 rounded-md p-4">
              {features?.length > 0 ? (
                features?.map((feature) => (
                  <div key={feature?.id} className="flex items-start gap-3 p-3 bg-card rounded-md border border-border hover:border-primary/30 transition-smooth">
                    <Checkbox
                      checked={selectedFeatures?.includes(feature?.id)}
                      onChange={() => handleFeatureToggle(feature?.id)}
                      disabled={isLoading}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{feature?.displayName}</p>
                      {feature?.description && (
                        <p className="text-xs text-muted-foreground mt-1">{feature?.description}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No features available</p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          {onClose && (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="default"
            loading={isLoading}
            disabled={isLoading || loadingData}
            iconName="UserPlus"
            className="flex-1"
          >
            {isLoading ? 'Creating User...' : 'Create User'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddUser;